import React from "react";
import { CheckCircle, AlertCircle, Calendar, ArrowUpRight, ArrowDownLeft, Eye, Receipt, Building, MapPin } from "lucide-react";
import { Transaction } from "../../constants/dataTypes";

interface TransactionsTableProps {
  transactions: Transaction[];
  loading: boolean;
  onViewDetails: (transaction: Transaction) => void;
}

const TransactionsTable: React.FC<TransactionsTableProps> = ({
  transactions,
  loading,
  onViewDetails,
}) => {
  const getStatusIcon = (status: Transaction["status"]) => {
    const iconProps = { size: 16 };
    switch (status) {
      case "succeeded":
        return <CheckCircle {...iconProps} className="text-green-500" />;
      case "failed":
        return <AlertCircle {...iconProps} className="text-red-500" />;
      case "refunded":
        return <CheckCircle {...iconProps} className="text-blue-500" />;
      case "pending":
        return <AlertCircle {...iconProps} className="text-yellow-500" />;
      case "processing":
        return <AlertCircle {...iconProps} className="text-blue-500" />;
      case "cancelled":
        return <AlertCircle {...iconProps} className="text-gray-500" />;
      default:
        return <AlertCircle {...iconProps} className="text-gray-500" />;
    }
  };

  const getTypeIcon = (transactionType: "credit" | "debit") => {
    const iconProps = { size: 16 };
    return transactionType === "credit" ? 
      <ArrowDownLeft {...iconProps} className="text-green-500" /> : 
      <ArrowUpRight {...iconProps} className="text-red-500" />;
  };

  const formatCurrency = (amount: number, currency: string = "USD") => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const getEntityTypeColor = (entityType: string) => {
    const colors: Record<string, string> = {
      subscription: "bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300",
      wallet_recharge: "bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300",
      order: "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300",
      invoice: "bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300",
    };
    return colors[entityType] || "bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300";
  };

  const getRefTypeIcon = (refType: Transaction["refType"]) => {
    const iconProps = { size: 14 };
    return refType === "company" ? 
      <Building {...iconProps} className="text-blue-500" /> : 
      <MapPin {...iconProps} className="text-green-500" />;
  };

  const getNetAmount = (transaction: Transaction) => {
    return transaction.amount - (transaction.amountRefunded || 0);
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
        <p className="text-gray-500 dark:text-gray-400 mt-4">Loading transactions...</p>
      </div>
    );
  }

  if (transactions.length === 0) {
    return (
      <div className="text-center py-12">
        <Receipt size={48} className="mx-auto text-gray-300 dark:text-gray-600 mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No transactions found</h3>
        <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
          No transactions match your current filters.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl overflow-hidden shadow-sm dark:shadow-gray-900/20">
      {/* Desktop Table */}
      <div className="hidden lg:block">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Date & Time</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Description & Reference</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Entity Type</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Amount</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {transactions.map((transaction) => (
              <tr key={transaction._id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-150">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    {new Date(transaction.createdAt).toLocaleDateString()}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {new Date(transaction.createdAt).toLocaleTimeString()}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2 mb-1">
                    {getRefTypeIcon(transaction.refType)}
                    <div className="text-sm font-medium text-gray-900 dark:text-white">{transaction.description}</div>
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
                    {transaction.chargeId && <div className="font-mono">Charge: {transaction.chargeId}</div>}
                    {transaction.receiptId && <div>Receipt: {transaction.receiptId}</div>}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getEntityTypeColor(transaction.entityType)}`}>
                    {transaction.entityType.replace('_', ' ')}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    {getTypeIcon(transaction.transactionType)}
                    <div className="text-right">
                      <span className={`text-sm font-semibold ${
                        transaction.transactionType === "credit" ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"
                      }`}>
                        {transaction.transactionType === "credit" ? "+" : "-"}{formatCurrency(getNetAmount(transaction), transaction.currency)}
                      </span>
                      {transaction.amountRefunded > 0 && (
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          Refunded: {formatCurrency(transaction.amountRefunded, transaction.currency)}
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    {getStatusIcon(transaction.status)}
                    <span className={`text-sm font-medium ${
                      transaction.status === "succeeded" ? "text-green-700 dark:text-green-400" : 
                      transaction.status === "failed" ? "text-red-700 dark:text-red-400" : 
                      transaction.status === "refunded" ? "text-blue-700 dark:text-blue-400" :
                      transaction.status === "pending" ? "text-yellow-700 dark:text-yellow-400" :
                      "text-gray-700 dark:text-gray-400"
                    }`}>
                      {transaction.status.charAt(0).toUpperCase() + transaction.status.slice(1)}
                    </span>
                  </div>
                  {transaction.markAsTest && (
                    <div className="text-xs text-orange-600 dark:text-orange-400 mt-1">Test Mode</div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <button 
                    onClick={() => onViewDetails(transaction)}
                    className="flex items-center gap-1 text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-200 p-2 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20"
                  >
                    <Eye size={16} />
                    <span className="text-sm">View</span>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="lg:hidden space-y-4 p-4">
        {transactions.map((transaction) => (
          <div key={transaction._id} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4 hover:shadow-md dark:hover:shadow-gray-900/20 transition-shadow duration-200">
            <div className="flex justify-between items-start mb-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  {getRefTypeIcon(transaction.refType)}
                  <h3 className="font-semibold text-gray-900 dark:text-white text-sm">{transaction.description}</h3>
                </div>
                <div className="space-y-1 text-xs text-gray-500 dark:text-gray-400 mb-2">
                  {transaction.chargeId && <div className="font-mono">Charge: {transaction.chargeId}</div>}
                  {transaction.receiptId && <div>Receipt: {transaction.receiptId}</div>}
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getEntityTypeColor(transaction.entityType)}`}>
                    {transaction.entityType.replace('_', ' ')}
                  </span>
                  <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                    <Calendar size={12} />
                    {new Date(transaction.createdAt).toLocaleDateString()}
                  </div>
                </div>
              </div>
              <div className="flex flex-col items-end gap-2">
                <div className="flex items-center gap-1">
                  {getTypeIcon(transaction.transactionType)}
                  <span className={`text-sm font-semibold ${
                    transaction.transactionType === "credit" ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"
                  }`}>
                    {transaction.transactionType === "credit" ? "+" : "-"}{formatCurrency(getNetAmount(transaction), transaction.currency)}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  {getStatusIcon(transaction.status)}
                  <span className={`text-xs font-medium ${
                    transaction.status === "succeeded" ? "text-green-700 dark:text-green-400" : 
                    transaction.status === "failed" ? "text-red-700 dark:text-red-400" : 
                    "text-yellow-700 dark:text-yellow-400"
                  }`}>
                    {transaction.status}
                  </span>
                </div>
                {transaction.markAsTest && (
                  <span className="text-xs text-orange-600 dark:text-orange-400">Test</span>
                )}
              </div>
            </div>
            <div className="flex justify-between items-center pt-3 border-t border-gray-100 dark:border-gray-700">
              <button 
                onClick={() => onViewDetails(transaction)}
                className="flex items-center gap-1 text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-200 text-sm"
              >
                <Eye size={14} />
                View Details
              </button>
              <button className="flex items-center gap-1 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors duration-200 text-sm">
                <Receipt size={14} />
                Receipt
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TransactionsTable;